import { useRef, DragEvent, ChangeEvent, useState } from 'react';
import { CircularProgress, SxProps, Theme } from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { DropZoneContainer, DropZoneText } from './styles';

const SIZES = {
	small: 24,
	medium: 32,
	large: 64,
};

type DropZoneProps = {
	sx?: SxProps<Theme>;
	size?: 'small' | 'medium' | 'large';
	onDrop: (files: FileList) => Promise<void> | void;
	multiple?: boolean;
	uploadText?: string;
};

export default function DropZone(props: DropZoneProps) {
	const { sx, size = 'medium', onDrop, multiple = false, uploadText = 'Upload file' } = props;

	const fileInputRef = useRef<HTMLInputElement>(null);
	const [isLoading, setIsLoading] = useState(false);

	const loadFiles = async (files: FileList) => {
		setIsLoading(true);
		try {
			await onDrop(files);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (isLoading) return;

		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			loadFiles(e.dataTransfer.files);
		}
	};

	const handleDragOver = (e: DragEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleClick = () => {
		if (isLoading) return;
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			loadFiles(e.target.files);
			if (fileInputRef.current) {
				fileInputRef.current.value = '';
			}
		}
	};

	return (
		<DropZoneContainer
			variant="outlined"
			onClick={handleClick}
			onDrop={handleDrop}
			onDragOver={handleDragOver}
			sx={sx}
			size={size}
			disabled={isLoading}
		>
			<input
				type="file"
				ref={fileInputRef}
				onChange={handleFileChange}
				style={{ display: 'none' }}
				multiple={multiple}
				disabled={isLoading}
			/>
			{isLoading && <CircularProgress size={SIZES[size]} />}
			{!isLoading && <FileUploadIcon sx={{ fontSize: SIZES[size] }} />}
			<DropZoneText size={size}>{isLoading ? 'Loading...' : uploadText}</DropZoneText>
		</DropZoneContainer>
	);
}
